/**
 * Audit Logger Service
 * Phase 2: Comprehensive audit trail for all expense mutations
 */

import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import {
  AuditLog,
  AuditAction,
  AuditEntityType,
  FieldChange,
  AuditLogOptions
} from '../types/audit';

/**
 * Main Audit Logger Class
 */
class AuditLogger {
  private collectionName = 'audit_log';

  /**
   * Log an action to the audit trail
   */
  async logAction(
    action: AuditAction,
    entityType: AuditEntityType,
    entityId: string,
    beforeValues?: Record<string, any>,
    afterValues?: Record<string, any>,
    options: AuditLogOptions = {}
  ): Promise<string | null> {
    // Skip if audit disabled
    if (options.skipAudit) {
      return null;
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        console.warn('⚠️ AuditLogger: No authenticated user, skipping audit log');
        return null;
      }

      // Calculate field-level changes
      const changes = this.calculateChanges(beforeValues, afterValues);

      // Build audit log entry
      const auditLog: AuditLog = {
        timestamp: Timestamp.now(),
        userId: user.uid,
        userName: user.displayName || undefined,
        userEmail: user.email || undefined,
        action,
        entityType,
        entityId,
        beforeValues,
        afterValues,
        changes,
        metadata: {
          ...options.customMetadata,
          isBulkOperation: !!options.bulkOperationId,
          bulkOperationId: options.bulkOperationId
        }
      };

      // Add searchable text
      auditLog.searchableText = this.buildSearchableText(auditLog);

      // Save to Firestore
      const docRef = await addDoc(collection(db, this.collectionName), auditLog);
      
      return docRef.id;
    } catch (error) {
      console.error('❌ AuditLogger: Failed to log audit:', error);
      // Don't throw - audit failures shouldn't break app functionality
      return null;
    }
  }

  /**
   * Log expense creation
   */
  async logExpenseCreate(
    expenseId: string,
    expenseData: Record<string, any>,
    options: AuditLogOptions = {}
  ): Promise<string | null> {
    return this.logAction(
      'CREATE',
      'expense',
      expenseId,
      undefined,
      expenseData,
      {
        ...options,
        customMetadata: {
          unitId: expenseData.unitId,
          unitName: expenseData.unit || expenseData.unitName,
          amount: expenseData.amount,
          ...options.customMetadata
        }
      }
    );
  }

  /**
   * Log expense update
   */
  async logExpenseUpdate(
    expenseId: string,
    beforeData: Record<string, any>,
    afterData: Record<string, any>,
    options: AuditLogOptions = {}
  ): Promise<string | null> {
    return this.logAction(
      'UPDATE',
      'expense',
      expenseId,
      beforeData,
      afterData,
      {
        ...options,
        customMetadata: {
          unitId: afterData.unitId || beforeData.unitId,
          unitName: afterData.unit || afterData.unitName || beforeData.unit,
          ...options.customMetadata
        }
      }
    );
  }

  /**
   * Log expense approval
   */
  async logExpenseApproval(
    expenseId: string,
    beforeData: Record<string, any>,
    afterData: Record<string, any>,
    options: AuditLogOptions = {}
  ): Promise<string | null> {
    return this.logAction(
      'APPROVE',
      'expense',
      expenseId,
      beforeData,
      afterData,
      {
        ...options,
        customMetadata: {
          unitId: afterData.unitId,
          approvalStatus: afterData.approvalStatus,
          approvalComment: options.customMetadata?.approvalComment,
          ...options.customMetadata
        }
      }
    );
  }

  /**
   * Log expense rejection
   */
  async logExpenseRejection(
    expenseId: string,
    beforeData: Record<string, any>,
    afterData: Record<string, any>,
    rejectionReason?: string,
    options: AuditLogOptions = {}
  ): Promise<string | null> {
    return this.logAction(
      'REJECT',
      'expense',
      expenseId,
      beforeData,
      afterData,
      {
        ...options,
        customMetadata: {
          unitId: afterData.unitId,
          approvalStatus: afterData.approvalStatus,
          approvalComment: rejectionReason,
          ...options.customMetadata
        }
      }
    );
  }

  /**
   * Log expense deletion
   */
  async logExpenseDelete(
    expenseId: string,
    expenseData: Record<string, any>,
    options: AuditLogOptions = {}
  ): Promise<string | null> {
    return this.logAction(
      'DELETE',
      'expense',
      expenseId,
      expenseData,
      undefined,
      {
        ...options,
        customMetadata: {
          unitId: expenseData.unitId,
          amount: expenseData.amount,
          ...options.customMetadata
        }
      }
    );
  }

  /**
   * Log expense restoration
   */
  async logExpenseRestore(
    expenseId: string,
    expenseData: Record<string, any>,
    options: AuditLogOptions = {}
  ): Promise<string | null> {
    return this.logAction(
      'RESTORE',
      'expense',
      expenseId,
      undefined,
      expenseData,
      {
        ...options,
        customMetadata: {
          unitId: expenseData.unitId,
          amount: expenseData.amount,
          ...options.customMetadata
        }
      }
    );
  }

  /**
   * Log production entry creation/update
   */
  async logProductionAction(
    action: AuditAction,
    entryId: string,
    beforeData?: Record<string, any>,
    afterData?: Record<string, any>,
    options: AuditLogOptions = {}
  ): Promise<string | null> {
    const data = afterData || beforeData;
    return this.logAction(
      action,
      'production',
      entryId,
      beforeData,
      afterData,
      {
        ...options,
        customMetadata: {
          unitId: data?.unitId,
          productName: data?.productName || data?.product,
          quantity: data?.quantity || data?.producedBags,
          ...options.customMetadata
        }
      }
    );
  }

  /**
   * Calculate field-level changes between before and after
   */
  private calculateChanges(
    before?: Record<string, any>,
    after?: Record<string, any>
  ): FieldChange[] {
    const changes: FieldChange[] = [];

    if (!before || !after) {
      return changes;
    }

    // Get all unique keys
    const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);

    // Skip internal Firebase fields
    const skipFields = ['createdAt', 'updatedAt', 'createdBy', 'updatedBy'];

    for (const key of allKeys) {
      if (skipFields.includes(key)) continue;

      const oldValue = before[key];
      const newValue = after[key];

      // Check if values are different
      if (!this.valuesEqual(oldValue, newValue)) {
        changes.push({
          field: key,
          oldValue: this.serializeValue(oldValue),
          newValue: this.serializeValue(newValue),
          type: this.getValueType(newValue)
        });
      }
    }

    return changes;
  }

  /**
   * Check if two values are equal (deep comparison)
   */
  private valuesEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (typeof a !== typeof b) return false;

    // Handle Timestamps
    if (a?.toDate && b?.toDate) {
      return a.toDate().getTime() === b.toDate().getTime();
    }

    // Handle arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((val, idx) => this.valuesEqual(val, b[idx]));
    }

    // Handle objects
    if (typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;
      return keysA.every(key => this.valuesEqual(a[key], b[key]));
    }

    return false;
  }

  /**
   * Serialize value for storage (convert Timestamps, etc.)
   */
  private serializeValue(value: any): any {
    if (value == null) return null;
    if (value?.toDate) return value.toDate().toISOString();
    if (value instanceof Date) return value.toISOString();
    if (Array.isArray(value)) return value.map(v => this.serializeValue(v));
    if (typeof value === 'object') {
      return Object.entries(value).reduce((acc, [k, v]) => {
        acc[k] = this.serializeValue(v);
        return acc;
      }, {} as Record<string, any>);
    }
    return value;
  }

  /**
   * Get value type for change tracking
   */
  private getValueType(value: any): FieldChange['type'] {
    if (value == null) return 'string';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';
    if (value?.toDate || value instanceof Date) return 'date';
    if (Array.isArray(value)) return 'array';
    if (typeof value === 'object') return 'object';
    return 'string';
  }

  /**
   * Build searchable text for full-text search
   */
  private buildSearchableText(log: AuditLog): string {
    const parts = [
      log.action,
      log.entityType,
      log.entityId,
      log.userName,
      log.userEmail,
      log.metadata?.unitName,
      log.metadata?.approvalComment
    ].filter(Boolean);

    return parts.join(' ').toLowerCase();
  }
}

// Export singleton instance
export const auditLogger = new AuditLogger();

// Export class for testing
export { AuditLogger };
